import React, { useEffect, useState } from "react";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { toast } from "sonner";
import { exportToExcel } from "../../utils/exportExcel";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../../components/ui/dialog";
import { CheckCircle, XCircle, Clock, CalendarDays, Download } from "lucide-react";

const statusColors = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

export default function LeaveManagement() {
  const [leaves, setLeaves] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [rejectLeave, setRejectLeave] = useState(null);
  const [rejReason, setRejReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchLeaves = () => {
    setLoading(true);
    api.get("/leaves").then((r) => setLeaves(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchLeaves(); }, []);

  const handleApprove = async (id) => {
    try {
      await api.put(`/leaves/${id}/approve`);
      toast.success("Leave approved");
      fetchLeaves();
    } catch {
      toast.error("Failed to approve leave");
    }
  };

  const handleReject = async () => {
    if (!rejReason.trim()) { toast.error("Rejection reason required"); return; }
    setActionLoading(true);
    try {
      await api.put(`/leaves/${rejectLeave}/reject`, { rejection_reason: rejReason });
      toast.success("Leave rejected");
      setRejectLeave(null);
      setRejReason("");
      fetchLeaves();
    } catch {
      toast.error("Failed to reject leave");
    } finally {
      setActionLoading(false);
    }
  };

  const tabs = ["all", "pending", "approved", "rejected"];
  const filtered = filter === "all" ? leaves : leaves.filter((l) => l.status === filter);

  const handleExport = () => {
    const toExport = filtered.length > 0 ? filtered : leaves;
    if (!toExport.length) { toast.error("No data to export"); return; }
    const sheet = toExport.map((l) => ({
      Employee: l.employee_name, "Start Date": l.start_date, "End Date": l.end_date,
      Days: Math.ceil((new Date(l.end_date) - new Date(l.start_date)) / 86400000) + 1,
      Reason: l.reason, Status: l.status,
      "Rejection Reason": l.rejection_reason || "",
      Submitted: new Date(l.created_at).toLocaleDateString("en-IN"),
    }));
    exportToExcel([{ data: sheet, name: "Leaves" }], `Leaves_${filter}`);
    toast.success("Leaves exported!");
  };

  const daysBetween = (start, end) => {
    const diff = new Date(end) - new Date(start);
    return Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
  };

  return (
    <Layout>
      <div className="p-8 page-enter">
        <div className="mb-7">
          <h1 className="text-2xl font-bold text-slate-900">Leave Management</h1>
          <p className="text-slate-500 text-sm mt-1">Review and manage employee leave requests</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
            {tabs.map((t) => {
              const count = t === "all" ? leaves.length : leaves.filter((l) => l.status === t).length;
              return (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-all ${
                    filter === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid={`leave-tab-${t}`}
                >
                  {t} <span className="ml-1 text-xs opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
          <Button
            onClick={handleExport}
            variant="outline"
            className="gap-2 border-slate-200 text-slate-700 hover:border-[#38b6e8] hover:text-[#38b6e8]"
            data-testid="export-leaves-btn"
          >
            <Download size={15} /> Export Excel
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <CalendarDays size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No {filter === "all" ? "" : filter} leave requests</p>
            </div>
          ) : (
            <table className="w-full" data-testid="leaves-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Employee", "Duration", "Days", "Reason", "Status", "Actions"].map((h) => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((leave) => (
                  <tr key={leave.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`leave-row-${leave.id}`}>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-slate-800 text-sm">{leave.employee_name}</p>
                      <p className="text-slate-400 text-xs">{new Date(leave.created_at).toLocaleDateString("en-IN")}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      <p className="font-medium">{leave.start_date}</p>
                      <p className="text-slate-400">to {leave.end_date}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className="bg-slate-100 text-slate-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                        {daysBetween(leave.start_date, leave.end_date)}d
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600 text-sm max-w-xs">
                      <p className="truncate">{leave.reason}</p>
                      {leave.rejection_reason && (
                        <p className="text-red-500 text-xs mt-0.5">Reason: {leave.rejection_reason}</p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <Badge className={`${statusColors[leave.status]} border text-xs font-medium capitalize`}>
                        {leave.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-4">
                      {leave.status === "pending" && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove(leave.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold transition-colors border border-emerald-200"
                            data-testid={`approve-leave-${leave.id}`}
                          >
                            <CheckCircle size={13} /> Approve
                          </button>
                          <button
                            onClick={() => { setRejectLeave(leave.id); setRejReason(""); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-xs font-semibold transition-colors border border-red-200"
                            data-testid={`reject-leave-${leave.id}`}
                          >
                            <XCircle size={13} /> Reject
                          </button>
                        </div>
                      )}
                      {leave.status !== "pending" && (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={!!rejectLeave} onOpenChange={() => setRejectLeave(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Leave Request</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <Label htmlFor="rej-reason" className="text-slate-700 font-medium text-sm">Reason for rejection *</Label>
            <Textarea
              id="rej-reason"
              value={rejReason}
              onChange={(e) => setRejReason(e.target.value)}
              placeholder="Explain why this leave is being rejected..."
              className="mt-2 border-slate-200 focus-visible:ring-[#38b6e8] resize-none"
              rows={4}
              data-testid="rejection-reason-input"
            />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRejectLeave(null)} className="border-slate-200">Cancel</Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-reject-btn"
            >
              {actionLoading ? "Rejecting..." : "Reject Leave"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
